import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Trash2, X, CheckSquare, FileBarChart } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const BulkActionBar = ({ selectedCount, selectedIds, docType, onComplete, onClear, label, extraActions }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleBulkDelete = async () => {
    setLoading(true);
    try {
      const res = await api.post('/archive/bulk-delete', { ids: selectedIds, doc_type: docType });
      toast.success(res.data.message || `${selectedCount} items archived`);
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally { setLoading(false); setConfirmOpen(false); }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-4 animate-fadeIn" data-testid="bulk-action-bar">
        <CheckSquare className="w-5 h-5 text-blue-400" />
        <span className="text-sm font-medium">{selectedCount} {label || 'item(s)'} selected</span>
        {(extraActions || []).map((action) => (
          <Button key={action.testId || action.label} size="sm" variant="ghost"
            className={action.className || 'text-emerald-300 hover:text-emerald-200 hover:bg-emerald-900/30 gap-1'}
            onClick={action.onClick} data-testid={action.testId}>
            {action.icon || <FileBarChart className="w-4 h-4" />} {action.label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-900/30 gap-1"
          onClick={() => setConfirmOpen(true)} data-testid="bulk-delete-btn">
          <Trash2 className="w-4 h-4" /> Delete Selected
        </Button>
        <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white" onClick={onClear} data-testid="bulk-clear-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {selectedCount} {label || 'items'}?</DialogTitle>
            <DialogDescription>
              These items will be moved to Archive. You can restore them later from the Archive page.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={loading}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={loading} onClick={handleBulkDelete} data-testid="confirm-bulk-delete">
              {loading ? 'Archiving...' : `Delete ${selectedCount} items`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkActionBar;
